import { Injectable } from '@angular/core';

import { AsyncJob, AsyncJobStatus } from '@recruitee/async-job-data';
import { I18nService } from '@recruitee/i18n';

import {
  AsyncJobConfig,
  AsyncJobDescriptionI18nParams,
  AsyncJobViewModel,
} from '../async-job-view.types';
import { AsyncJobConfigService } from '../configs/async-job-config.service';

@Injectable({ providedIn: 'root' })
export class AsyncJobViewModelService {
  constructor(
    private i18nService: I18nService,
    private asyncJobConfig: AsyncJobConfigService,
  ) {}

  public create(job: AsyncJob): AsyncJobViewModel {
    const total = job.total || 1;
    const config = this.asyncJobConfig.get(job);

    return {
      uuid: job.uuid,
      type: job.type,
      errors: job.errors,
      count: job.count,
      done: job.status === AsyncJobStatus.FINISHED,
      working: job.status === AsyncJobStatus.WORKING,
      actionButtonLabel: config.actionButtonLabel!,
      total: total,
      pctProgress: Math.round(this.getProgress(job) * 100),
      skipProgressBar: config.skipProgressBar,
      ...this.produceJobDescription(job, total),
    };
  }

  private produceJobDescription(
    job: AsyncJob,
    total: number,
  ): {
    title: string;
    message?: string;
  } {
    const jobConfig = this.asyncJobConfig.get(job);
    const { translations } = jobConfig;
    const working = job.status !== AsyncJobStatus.FINISHED;

    const counts: Omit<AsyncJobDescriptionI18nParams, 'taskName'> = {
      errorsCount: job.errors.length,
      currentCount: job.count,
      currentPercent: Math.round(this.getProgress(job) * 100),
      totalCount: total,
      count: total,
    };
    const taskName = this.i18nService.translate(translations.title.progress, counts);
    const i18nParams = { ...counts, taskName };

    if (working) {
      return this.getInProgressDescription(jobConfig, i18nParams);
    } else if (counts.errorsCount === 0) {
      return this.getSuccessDescription(jobConfig, i18nParams);
    } else if (counts.errorsCount < total) {
      return this.getSomeFailedDescription(jobConfig, i18nParams);
    } else {
      return this.getAllFailedDescription(jobConfig, i18nParams);
    }
  }

  private getInProgressDescription(
    { translations }: AsyncJobConfig,
    params: AsyncJobDescriptionI18nParams,
  ) {
    const { message } = translations;
    return {
      title: params.taskName,
      ...(message?.progress ? { message: this.i18nService.translate(message.progress) } : {}),
    };
  }

  private getSuccessDescription(
    { translations }: AsyncJobConfig,
    params: AsyncJobDescriptionI18nParams,
  ) {
    const { title, message } = translations;
    const titleKey = title.success ?? 'app.background_job_progress.default_completed.title';
    return {
      title: this.i18nService.translate(titleKey, params),
      ...(message?.success ? { message: this.i18nService.translate(message.success) } : {}),
    };
  }

  private getSomeFailedDescription(
    { translations }: AsyncJobConfig,
    params: AsyncJobDescriptionI18nParams,
  ) {
    const titleKey =
      translations.completedWithErrors?.title ??
      'app.background_job_progress.default_completed_with_errors.title';
    return { title: this.i18nService.translate(titleKey, params) };
  }

  private getAllFailedDescription(
    { translations }: AsyncJobConfig,
    params: AsyncJobDescriptionI18nParams,
  ) {
    const titleKey =
      translations.failed?.title ?? 'app.background_job_progress.default_failed.title';
    return { title: this.i18nService.translate(titleKey, params) };
  }

  private getProgress(job: AsyncJob) {
    return Math.floor((job.count / job.total) * 100) / 100;
  }
}
